import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';
import TastingNoteForm from './TastingNoteForm';
import SessionView from './SessionView';
import './TastingView.css';

const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(item => item && !item.$ref);
};

function TastingView({ instance, showMessage }) {
  const { t } = useTranslation();
  const [subView, setSubView] = useState('list'); // 'list' | 'newNote' | 'newSession' | 'sessionDetail'
  const [notes, setNotes] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [notesResult, sessionsResult] = await Promise.allSettled([
        apiFetchJson(instance, '/api/tasting/notes'),
        apiFetchJson(instance, '/api/tasting/sessions'),
      ]);
      if (notesResult.status === 'fulfilled') setNotes(unwrap(notesResult.value));
      if (sessionsResult.status === 'fulfilled') setSessions(unwrap(sessionsResult.value));
    } catch (e) {
      console.error('Failed to load tasting data:', e);
      setError(t('grapeEscape.tasting.loadError', 'Failed to load tasting data.'));
    } finally {
      setLoading(false);
    }
  }, [instance, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleNoteSaved = useCallback(() => {
    setSubView('list');
    loadData();
    showMessage(t('grapeEscape.tasting.noteSaved', 'Tasting note saved! 📝'));
  }, [loadData, showMessage, t]);

  const handleSessionCreated = useCallback(async (sessionData) => {
    try {
      await apiFetchJson(instance, '/api/tasting/sessions', {
        method: 'POST',
        body: JSON.stringify(sessionData),
      });
      setSubView('list');
      loadData();
      showMessage(t('grapeEscape.tasting.sessionCreated', 'Tasting session created! 🎉'));
    } catch (e) {
      console.error('Failed to create session:', e);
      showMessage(t('grapeEscape.tasting.sessionError', 'Failed to create session.'), 'error');
    }
  }, [instance, loadData, showMessage, t]);

  const openSession = useCallback((session) => {
    setSelectedSession(session);
    setSubView('sessionDetail');
  }, []);

  // New tasting note form
  if (subView === 'newNote') {
    return (
      <TastingNoteForm
        instance={instance}
        onSave={handleNoteSaved}
        onCancel={() => setSubView('list')}
        showMessage={showMessage}
      />
    );
  }

  // Session detail
  if (subView === 'sessionDetail' && selectedSession) {
    return (
      <SessionView
        instance={instance}
        session={selectedSession}
        onBack={() => { setSubView('list'); setSelectedSession(null); }}
        onUpdate={loadData}
        showMessage={showMessage}
      />
    );
  }

  // New session inline form
  if (subView === 'newSession') {
    return (
      <NewSessionForm
        onSave={handleSessionCreated}
        onCancel={() => setSubView('list')}
      />
    );
  }

  return (
    <div className="ge-tasting">
      {/* Action Buttons */}
      <div className="ge-tasting-actions">
        <button className="ge-btn-primary" onClick={() => setSubView('newNote')}>
          📝 {t('grapeEscape.tasting.newNote', 'New Tasting')}
        </button>
        <button className="ge-btn-secondary" onClick={() => setSubView('newSession')}>
          👥 {t('grapeEscape.tasting.newSession', 'New Session')}
        </button>
      </div>

      {error && (
        <div className="ge-error">
          <span>{error}</span>
          <button onClick={loadData}>↻</button>
        </div>
      )}

      {loading ? (
        <div className="ge-loading">
          <div className="ge-spinner" />
          <span>{t('grapeEscape.tasting.loading', 'Loading...')}</span>
        </div>
      ) : (
        <>
          {/* My Notes */}
          <div className="ge-section">
            <h3>{t('grapeEscape.tasting.myNotes', 'My Notes')}</h3>
            {notes.length === 0 ? (
              <div className="ge-empty">
                {t('grapeEscape.tasting.noNotes', 'No tasting notes yet. Record your first impressions!')}
              </div>
            ) : (
              <div className="ge-notes-list">
                {notes.map(note => (
                  <div key={note.id} className="ge-tasting-note-card ge-card">
                    <div className="ge-note-row">
                      <span className="ge-note-wine-name">{note.wineName || 'Unknown wine'}</span>
                      <span className="ge-note-date">
                        {note.tastingDate ? new Date(note.tastingDate).toLocaleDateString() : ''}
                      </span>
                    </div>
                    <div className="ge-note-row">
                      <span className="ge-note-rating-stars">
                        {'⭐'.repeat(Math.min(note.rating || 0, 5))}
                        {note.rating ? ` (${note.rating}/5)` : ''}
                      </span>
                      {note.qualityLevel && (
                        <span className="ge-quality-badge">{note.qualityLevel}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sessions */}
          <div className="ge-section">
            <h3>{t('grapeEscape.tasting.sessions', 'Sessions')}</h3>
            {sessions.length === 0 ? (
              <div className="ge-empty">
                {t('grapeEscape.tasting.noSessions', 'No tasting sessions yet. Organize one with friends!')}
              </div>
            ) : (
              <div className="ge-sessions-list">
                {sessions.map(session => (
                  <div
                    key={session.id}
                    className="ge-session-card ge-card"
                    onClick={() => openSession(session)}
                  >
                    <div className="ge-session-row">
                      <span className="ge-session-name">{session.name}</span>
                      <span className={`ge-session-type-badge ${(session.type || '').toLowerCase()}`}>
                        {session.type || 'Open'}
                      </span>
                    </div>
                    <div className="ge-session-meta">
                      <span>{session.date ? new Date(session.date).toLocaleDateString() : ''}</span>
                      <span className={`ge-session-status ${(session.status || '').toLowerCase()}`}>
                        {session.status || 'Draft'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* Inline New Session Form */
function NewSessionForm({ onSave, onCancel }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [type, setType] = useState('Open');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [isBlind, setIsBlind] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ name, type, date, isBlind });
  };

  return (
    <div className="ge-new-session">
      <div className="ge-section-header">
        <h3>{t('grapeEscape.tasting.createSession', 'Create Tasting Session')}</h3>
        <button className="ge-btn-secondary" onClick={onCancel}>
          {t('grapeEscape.tasting.cancel', 'Cancel')}
        </button>
      </div>
      <form onSubmit={handleSubmit} className="ge-card">
        <div className="ge-form-grid">
          <div className="ge-form-group">
            <label>{t('grapeEscape.tasting.sessionName', 'Session Name')} *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} required />
          </div>
          <div className="ge-form-group">
            <label>{t('grapeEscape.tasting.sessionType', 'Type')}</label>
            <select value={type} onChange={e => setType(e.target.value)}>
              <option value="Open">Open</option>
              <option value="Blind">Blind</option>
              <option value="Semi-Blind">Semi-Blind</option>
              <option value="Themed">Themed</option>
            </select>
          </div>
          <div className="ge-form-group">
            <label>{t('grapeEscape.tasting.sessionDate', 'Date')}</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div className="ge-form-group">
            <label>
              <input
                type="checkbox"
                checked={isBlind}
                onChange={e => setIsBlind(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              {t('grapeEscape.tasting.blindTasting', 'Blind Tasting')}
            </label>
          </div>
        </div>
        <div className="ge-form-actions">
          <button type="submit" className="ge-btn-primary">
            {t('grapeEscape.tasting.create', 'Create Session')}
          </button>
          <button type="button" className="ge-btn-secondary" onClick={onCancel}>
            {t('grapeEscape.tasting.cancel', 'Cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default TastingView;
